import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';

import { Grid } from 'antd';

import { productValidator } from '@eduzz-product-manager/validators';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { FormBlock } from '@/components/FormBlock';
import { useZustandSync } from '@/hooks/useZustandSync';
import { trpc } from '@/lib/trpc';
import useAIProductExampleStore from '@/stores/AIProductExampleStore';
import useProductStore, { productStore } from '@/stores/productStore';

import { Select } from '../form/Select';
import { NiceActions } from '../NiceActions';
import ReviewCategory from './ReviewMode/ReviewCategory';

const categorySchema = z.object({
  categories: productValidator.shape.categories.unwrap()
});

type CategoryFormValues = z.infer<typeof categorySchema>;

const MAX_CATEGORIES = 5;

export default function Categories() {
  const store = productStore.getState();
  const screens = Grid.useBreakpoint();

  const { data: allCategories } = trpc.product.categories.useQuery(undefined, {
    gcTime: 1000 * 60 * 10
  });

  const form = useForm<CategoryFormValues>({
    resolver: zodResolver(categorySchema),
    mode: 'onChange',
    defaultValues: { categories: useProductStore(d => d['categories']) }
  });

  useZustandSync<CategoryFormValues>(form.control, data => ({
    categories: data['categories']
  }));

  const initialValue = useRef(form.control._defaultValues['categories']);
  const categories = useProductStore(d => d['categories'] || []);
  const aiProductExampleStore = useAIProductExampleStore(d => d);
  const showIaButton = aiProductExampleStore.hasData();

  useEffect(() => {
    if (categories.every(value => initialValue.current?.some(v => v === value))) {
      return;
    }

    store.setIaField('categories', false);
  }, [categories, store]);

  return (
    <FormBlock
      name='categories'
      form={form}
      title='Categorias'
      lengthIndicator={`${categories.length}/${MAX_CATEGORIES}`}
      preview={<ReviewCategory />}
    >
      <Select
        id='categories'
        modalTitle='Categoria'
        values={allCategories?.map(category => ({ label: category.label, value: String(category.id) })) ?? []}
        maxItems={MAX_CATEGORIES}
        multiple
        placeholder='Selecione as categorias'
        name={'categories'}
        onFocus={event => {
          if (screens.lg) {
            return;
          }

          const elementTopPosition = event.target.getBoundingClientRect().top;

          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              document.querySelector('.steps-scroll-container')?.scrollTo({
                behavior: 'smooth',
                top: elementTopPosition
              });
            });
          });
        }}
      />

      {showIaButton && (
        <NiceActions
          onAIGenerate={() => {
            const example = aiProductExampleStore.getExampleOf('categories');

            if (example) {
              initialValue.current = example;
              form.setValue('categories', example);
              store.setIaField('categories', true);
            }
          }}
        />
      )}
    </FormBlock>
  );
}
